import { ColorMatrixFilter, Container, Graphics, Sprite, Text, type Texture } from 'pixi.js';
import { FRIEND_GAME_LABEL, type BoardRunCell, type RunPot } from '@goodtime/shared';
import { formatShort } from '../../logic/road/format';
import { hubPair, ringLayout } from '../../logic/road/ringLayout';
import { glowTexture } from './glow';

const FONT = 'Fredoka, system-ui, -apple-system, sans-serif';

/** The painted pieces a cell is made of; a friend's lamp also shows the
 *  friend (`friend_<id>`) while dark and their game (`game_<kind>`) once lit. */
export type RingArt =
  | 'bezel' | 'lamp' | 'coin' | 'spin' | 'silver' | 'bomb' | 'lock'
  | `friend_${string}` | `game_${string}`;

/** Each prize's picture in its brass bezel, as a share of the cell. */
const ICON: Record<string, { art: RingArt; size: number }> = {
  coins_small: { art: 'coin', size: 0.95 },
  coins_big: { art: 'coin', size: 1.15 },
  free_spins: { art: 'spin', size: 1.0 },
  silver: { art: 'silver', size: 1.15 },
  bomb: { art: 'bomb', size: 1.15 },
};

/** Drawn stand-ins while the art loads (or for a slot without it). */
const GLYPH: Record<string, string> = { bomb: '💣', free_spins: '★', silver: '●', coins_small: '●', coins_big: '●' };

/** Where the painted rings (assets/road/ring_<world>.png, one template, 720
 *  px) put things, as shares of the ring's width: the band's middle, where
 *  the cells go (0.33 to 0.34 on each ring); the hole's lift above the
 *  art's centre (6 to 8 px of 720); and the hub plate's width, so its rim
 *  (93% of the plate's art) sits just over the hole's edge, 0.25 from its
 *  centre, with the rope round it showing evenly. */
const CELL_RING = 0.34;
const HOLE_LIFT = 0.0094;
const HUB = 0.539;

/** Dark brown, for text outlines and the wood a cell sits in. */
const INK = 0x2a1605;
const WELL = 0x2e1b0a;

interface CellView {
  root: Container;
  plate: Graphics;
  icon: Container;
  value: Text;
}

export interface BoardRingOptions {
  /** Painted ring art for the slot; a drawn ring stands in until it loads
   *  (see {@link BoardRing.setRingTexture}) or when the slot has none. */
  ringTexture?: Texture | null;
  coinTexture?: Texture | null;
  /** The cells' painted pieces, looked up as each cell is drawn: the road
   *  art loads after the board is built. Missing art falls back to drawn
   *  shapes. */
  art?: (name: RingArt) => Texture | null;
}

/**
 * The Lamp Run board: dims the reels and lays twelve lamps round a ring,
 * with the run pot in the hub. A glow marks the light's cell; the host moves
 * it with {@link setLight} and marks landings with {@link flashCell}.
 */
export class BoardRing extends Container {
  private readonly scrim = new Graphics();
  private ring: Sprite | Graphics;
  private readonly cellsLayer = new Container();
  private readonly light: Sprite;
  private readonly title: Text;
  /** The plate under the run pot (a slot's painted hub), when it has one. */
  private readonly hub = new Sprite();
  private hubSize = 0;
  /** The hub's centre, where the label and the pot are centred as a pair. */
  private hubCy = 0;
  private readonly hubLabel: Text;
  private readonly hubValue: Text;
  private readonly coinTexture: Texture | null;
  private readonly art: (name: RingArt) => Texture | null;
  /** Unlit lamps: the lens greyed and darkened, as on the panel. */
  private readonly unlit = new ColorMatrixFilter();
  private cells: CellView[] = [];
  private points: { x: number; y: number }[] = [];
  private cellRadius = 20;

  constructor(options: BoardRingOptions = {}) {
    super();
    this.coinTexture = options.coinTexture ?? null;
    this.art = options.art ?? (() => null);
    this.unlit.desaturate();
    this.unlit.brightness(0.45, true);
    this.ring = options.ringTexture ? new Sprite(options.ringTexture) : new Graphics();
    if (this.ring instanceof Sprite) this.ring.anchor.set(0.5);
    this.light = new Sprite(glowTexture());
    this.light.anchor.set(0.5);
    this.light.tint = 0xfff1a8;
    this.light.blendMode = 'add';
    this.title = text('LAMP RUN', 15, 0xffe48a);
    this.hubLabel = text('RUN POT', 10, 0xffffff);
    this.hubLabel.alpha = 0.75;
    this.hubValue = text('empty', 15, 0xffffff);
    // Placed by their tops: the pot grows a line for each kind of prize.
    this.hubLabel.anchor.set(0.5, 0);
    this.hubValue.anchor.set(0.5, 0);
    this.hub.anchor.set(0.5);
    this.hub.visible = false;
    this.addChild(this.scrim, this.ring, this.hub, this.light, this.cellsLayer, this.title, this.hubLabel, this.hubValue);
    this.visible = false;
  }

  /** Swap the drawn ring for the painted one. The ring art loads with the
   *  full theme, after the board is built, so the host passes it in when a
   *  run starts. */
  setRingTexture(texture: Texture | null): void {
    if (!texture) return;
    if (this.ring instanceof Sprite) {
      this.ring.texture = texture;
      return;
    }
    const sprite = new Sprite(texture);
    sprite.anchor.set(0.5);
    const index = this.getChildIndex(this.ring);
    this.removeChild(this.ring).destroy();
    this.ring = sprite;
    this.addChildAt(sprite, index);
  }

  /** The slot's painted plate under the run pot, or none. */
  setHubTexture(texture: Texture | null): void {
    if (texture) this.hub.texture = texture;
    this.hub.visible = !!texture;
    this.hub.width = this.hub.height = this.hubSize;
  }

  /** Build the cells for a run. */
  setRun(cells: readonly BoardRunCell[], lucky: boolean): void {
    this.cellsLayer.removeChildren().forEach((c) => c.destroy({ children: true }));
    this.cells = cells.map((cell) => this.buildCell(cell, lucky));
    this.title.text = lucky ? 'LUCKY RUN' : 'LAMP RUN';
    // The info bar over the panel names the run; the ring shows the pot only.
    this.title.visible = false;
  }

  /** Centre the board on the reels, `size` across, and dim `reels`. */
  layout(cx: number, cy: number, size: number, reels: { x: number; y: number; w: number; h: number }): void {
    this.scrim.clear().roundRect(reels.x, reels.y, reels.w, reels.h, 12).fill({ color: 0x0d0703, alpha: 0.72 });
    const radius = size * CELL_RING;
    this.cellRadius = Math.max(14, size * 0.075);
    if (this.ring instanceof Sprite) {
      this.ring.x = cx;
      this.ring.y = cy;
      this.ring.width = size;
      this.ring.height = size;
    } else {
      this.ring
        .clear()
        .circle(cx, cy, radius)
        .stroke({ color: 0x5a3416, width: this.cellRadius * 1.6, alpha: 0.95 })
        .circle(cx, cy, radius + this.cellRadius * 0.8)
        .stroke({ color: 0xffd36b, width: 2 })
        .circle(cx, cy, radius - this.cellRadius * 0.8)
        .stroke({ color: 0xffd36b, width: 2 });
    }
    this.points = ringLayout(this.cells.length || 12, cx, cy, radius);
    this.cells.forEach((cell, i) => {
      const p = this.points[i];
      if (!p) return;
      cell.root.x = p.x;
      cell.root.y = p.y;
      this.drawCell(cell);
    });
    this.light.width = this.cellRadius * 4;
    this.light.height = this.cellRadius * 4;
    this.title.x = cx;
    this.title.y = cy - radius * 0.42;
    // The plate sits in the painted ring's hole, which is a little above the
    // art's centre; the pot is centred on the plate.
    const hubCy = this.ring instanceof Sprite ? cy - size * HOLE_LIFT : cy;
    this.hubSize = size * HUB;
    this.hub.position.set(cx, hubCy);
    this.hub.width = this.hub.height = this.hubSize;
    this.hubLabel.x = cx;
    this.hubValue.x = cx;
    this.hubCy = hubCy;
    this.placeHub();
  }

  /** The label and the pot stay a centred pair on the plate, whatever the
   *  number of lines in the pot. */
  private placeHub(): void {
    const { labelY, valueY } = hubPair(this.hubCy, this.hubLabel.height, this.hubValue.height);
    this.hubLabel.y = labelY;
    this.hubValue.y = valueY;
  }

  /** Put the chasing light on a cell. */
  setLight(cell: number): void {
    const p = this.points[cell];
    if (!p) return;
    this.light.x = p.x;
    this.light.y = p.y;
  }

  /** Where the run pot shows (the hub), in canvas pixels: coins paid into
   *  the pot fly here. */
  get potCentre(): { x: number; y: number } {
    return this.toGlobal({ x: this.hubValue.x, y: this.hubValue.y + this.hubValue.height / 2 });
  }

  /** The run pot, one prize per line. */
  setPot(pot: RunPot): void {
    const lines: string[] = [];
    if (pot.coins > 0) lines.push(formatShort(pot.coins));
    if (pot.gems > 0) lines.push(`${pot.gems} gems`);
    if (pot.freeSpins > 0) lines.push(`+${pot.freeSpins} free`);
    this.hubValue.text = lines.length ? lines.join('\n') : 'empty';
    this.hubValue.alpha = lines.length ? 1 : 0.6;
    this.placeHub();
  }

  /** Mark a landing: prizes pop, a bomb shudders. */
  flashCell(cell: number, kind: string): void {
    const view = this.cells[cell];
    if (!view) return;
    const start = performance.now();
    const bomb = kind === 'bomb';
    const step = (now: number) => {
      const t = Math.min(1, (now - start) / 420);
      const wave = Math.sin(t * Math.PI);
      view.root.scale.set(1 + (bomb ? 0.05 : 0.28) * wave);
      view.root.x = (this.points[cell]?.x ?? view.root.x) + (bomb ? Math.sin(t * 40) * 4 * (1 - t) : 0);
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  private buildCell(cell: BoardRunCell, lucky: boolean): CellView {
    const root = new Container();
    const plate = new Graphics();
    const icon = new Container();
    const value = text('', 11, 0xffffff);
    root.addChild(plate, icon, value);
    root.label = cell.kind;
    (root as Container & { cellData?: BoardRunCell }).cellData = cell;
    root.alpha = lucky && cell.kind === 'bomb' ? 0.35 : 1;
    this.cellsLayer.addChild(root);
    return { root, plate, icon, value };
  }

  private drawCell(view: CellView): void {
    const cell = (view.root as Container & { cellData?: BoardRunCell }).cellData;
    if (!cell) return;
    const r = this.cellRadius;
    const dark = !cell.lit;
    const lamp = cell.kind.startsWith('lamp');
    view.icon.removeChildren().forEach((c) => c.destroy());
    const sprite = (name: RingArt, size: number, y = 0): Sprite | null => {
      const texture = name === 'coin' ? (this.art('coin') ?? this.coinTexture) : this.art(name);
      if (!texture) return null;
      const s = new Sprite(texture);
      s.anchor.set(0.5);
      s.width = size;
      s.height = size;
      s.y = y;
      view.icon.addChild(s);
      return s;
    };

    // Every cell is brass on wood: a lamp is its lens; a prize sits in a
    // bezel over a dark wooden well.
    view.plate.clear().circle(0, 0, r * 0.86).fill({ color: lamp && dark ? 0x1a0f05 : WELL });
    if (lamp) {
      const lens = sprite('lamp', r * 2);
      if (lens && dark) lens.filters = [this.unlit];
      if (!lens) view.plate.stroke({ color: 0xd9a441, width: 3 });
      if (dark) {
        // Whose lamp it is, in shadow, with the lock over it.
        const who = cell.friend ? sprite(`friend_${cell.friend}`, r * 1.35) : null;
        if (who) {
          who.tint = 0x000000;
          who.alpha = 0.5;
        }
        const lock = sprite('lock', r * (who ? 0.6 : 0.75));
        if (lock && who) {
          lock.x = r * 0.42;
          lock.y = r * 0.42;
        }
        if (!lock) this.glyph(view, '🔒', r * 0.6);
      } else if (cell.game && !sprite(`game_${cell.game}`, r * 1.3, -r * 0.1)) {
        this.glyph(view, '★', r * 0.8);
      }
    } else {
      if (!sprite('bezel', r * 2)) view.plate.stroke({ color: 0xd9a441, width: 3 });
      const icon = ICON[cell.kind];
      const picture = icon ? sprite(icon.art, r * icon.size, -r * 0.1) : null;
      if (!picture) this.glyph(view, GLYPH[cell.kind] ?? '●', r * 0.8);
      if (picture && cell.kind === 'silver' && !this.art('silver')) picture.tint = 0xdfe6f0;
    }

    const label =
      cell.kind === 'bomb' ? '-1'
      : cell.kind === 'free_spins' ? `${cell.value ?? ''} FREE`
      : lamp && !dark && cell.game ? FRIEND_GAME_LABEL[cell.game]
      : cell.value ? formatShort(cell.value)
      : '';
    view.value.text = label;
    view.value.style.fontSize = Math.max(9, r * 0.45);
    view.value.y = r * 0.62;
  }

  private glyph(view: CellView, glyph: string, size: number): void {
    const g = text(glyph, size, 0xffe8b0);
    g.y = -size * 0.12;
    view.icon.addChild(g);
  }
}

function text(value: string, size: number, color: number): Text {
  const t = new Text({
    text: value,
    style: { fontFamily: FONT, fontSize: size, fontWeight: '900', fill: color, align: 'center', stroke: { color: INK, width: 3 } },
  });
  t.anchor.set(0.5);
  return t;
}
